/**
 * features/landing/landingCss.ts
 * ------------------------------------------------------------------
 * The company page's stylesheet, injected by Landing.tsx.
 *
 * Kept out of Tailwind on purpose: this is a self-contained marketing
 * surface with its own light/dark bands, type scale and motion, and it
 * must not shift when the app's dark-mode class toggles.
 *
 * Design language:
 *   - Editorial rhythm — eyebrow, display heading, orange hairline,
 *     generous alternating bands (warm paper / white / charcoal).
 *   - Product-led proof — every claim sits beside the screen it
 *     happens on, in a floating browser frame.
 *   - Acepsle's signature: SHARP buttons, softly rounded everything.
 * All motion is disabled under prefers-reduced-motion.
 * ------------------------------------------------------------------
 */
import { FRAME_CSS } from '@/features/landing/frameCss';

export const LANDING_CSS = `
.ac {
  --b:#ff6b35; --bh:#ea5c28; --bs:#fff1ea;
  --ink:#1a1f26; --ink2:#22272e; --muted:#5b626b;
  --paper:#faf8f5; --white:#fff; --char:#161a20; --char2:#22272e;
  --line:#e9e4dd; --lineD:rgba(255,255,255,.10);
  --wrap:1180px;
  --pad:clamp(72px, 9vw, 118px);
  font-family:Inter,system-ui,-apple-system,sans-serif;
  color:var(--ink); background:var(--paper);
  overflow-x:hidden; -webkit-font-smoothing:antialiased;
}
.ac *,.ac *::before,.ac *::after { box-sizing:border-box; }
.ac h1,.ac h2,.ac h3,.ac h4 { font-family:Poppins,Inter,sans-serif; letter-spacing:-.025em; margin:0; }
.ac p { margin:0; }
.ac a { text-decoration:none; color:inherit; }
.ac-wrap { max-width:var(--wrap); margin:0 auto; padding:0 clamp(20px,5vw,40px); }
/* Anchor targets must clear the fixed nav. */
.ac [id] { scroll-margin-top:92px; }
html:has(.ac) { scroll-behavior:smooth; }
.ac-band { padding:var(--pad) 0; position:relative; }
.ac-paper { background:var(--paper); }
.ac-white { background:var(--white); }
.ac-dark { background:var(--char); color:#e8e9ec; }
.ac-dark h1,.ac-dark h2,.ac-dark h3,.ac-dark h4 { color:#fff; }
.ac-center { text-align:center; }

/* ---- Type ---- */
.ac-eyebrow {
  display:inline-flex; align-items:center; gap:8px;
  font-size:11px; font-weight:600; text-transform:uppercase;
  letter-spacing:.28em; color:var(--b);
}
.ac-rule { height:1px; width:56px; background:var(--b); margin:20px 0; }
.ac-rule.mid { margin-left:auto; margin-right:auto; }
.ac-h1 { font-size:clamp(38px,6.4vw,72px); font-weight:700; line-height:1.03; }
.ac-h2 { font-size:clamp(29px,4vw,46px); font-weight:700; line-height:1.1; }
.ac-h3 { font-size:clamp(20px,2.4vw,26px); font-weight:600; line-height:1.25; }
.ac-lead { font-size:clamp(16px,1.5vw,18.5px); line-height:1.68; color:var(--muted); max-width:40rem; }
.ac-dark .ac-lead { color:rgba(255,255,255,.66); }
.ac-lead.mid { margin-left:auto; margin-right:auto; }

/* ---- Buttons (sharp — the Acepsle signature) ---- */
.ac-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:9px;
  border:1px solid transparent; border-radius:0; cursor:pointer;
  padding:15px 30px; font-family:Inter,sans-serif; font-size:12.5px; font-weight:700;
  text-transform:uppercase; letter-spacing:.14em; white-space:nowrap;
  transition:background .25s ease, color .25s ease, border-color .25s ease, transform .25s ease, box-shadow .25s ease;
}
.ac-btn svg { transition:transform .25s ease; }
.ac-btn:hover svg { transform:translateX(3px); }
.ac-btn-brand { background:var(--b); color:#fff; box-shadow:0 14px 34px -14px rgba(255,107,53,.75); }
.ac-btn-brand:hover { background:var(--bh); transform:translateY(-2px); box-shadow:0 20px 40px -14px rgba(255,107,53,.8); }
.ac-btn-line { border-color:rgba(255,255,255,.32); color:#fff; background:transparent; }
.ac-btn-line:hover { border-color:var(--b); color:var(--b); }
.ac-btn-dark { background:var(--ink2); color:#fff; }
.ac-btn-dark:hover { background:#000; transform:translateY(-2px); }
.ac-btn-sm { padding:11px 20px; font-size:11px; letter-spacing:.12em; }
.ac-btn-ghost { background:transparent; color:rgba(255,255,255,.82); }
.ac-btn-ghost:hover { color:#fff; }

/* ---- Reveal on scroll ---- */
.ac-reveal { opacity:0; transform:translateY(24px); transition:opacity .75s ease, transform .75s cubic-bezier(.2,.7,.2,1); }
.ac-reveal.in { opacity:1; transform:none; }
.ac-reveal.d1 { transition-delay:.08s; } .ac-reveal.d2 { transition-delay:.16s; }
.ac-reveal.d3 { transition-delay:.24s; } .ac-reveal.d4 { transition-delay:.32s; }

/* ---- Sticky nav ---- */
.ac-nav {
  position:fixed; top:0; left:0; right:0; z-index:50;
  transition:background .35s ease, box-shadow .35s ease, backdrop-filter .35s ease;
}
.ac-nav-row { display:flex; align-items:center; gap:20px; height:78px; }
.ac-nav.stuck {
  background:rgba(22,26,32,.86); -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px);
  box-shadow:0 1px 0 rgba(255,255,255,.08);
}
.ac-nav-links { display:flex; align-items:center; gap:30px; margin-left:auto; }
.ac-nav-links a {
  font-size:12px; font-weight:600; letter-spacing:.14em; text-transform:uppercase;
  color:rgba(255,255,255,.68); transition:color .2s ease; position:relative; padding:6px 0;
}
.ac-nav-links a::after {
  content:""; position:absolute; left:0; right:100%; bottom:0; height:1px;
  background:var(--b); transition:right .3s cubic-bezier(.2,.7,.2,1);
}
.ac-nav-links a:hover { color:#fff; }
.ac-nav-links a:hover::after { right:0; }
.ac-nav-cta { display:flex; align-items:center; gap:10px; margin-left:8px; }
.ac-burger { display:none; background:none; border:0; color:#fff; padding:8px; cursor:pointer; margin-left:auto; }
.ac-mobile { display:none; }

/* ---- Hero ---- */
.ac-hero {
  position:relative; background:var(--char); color:#fff; overflow:hidden;
  padding:clamp(128px,15vw,176px) 0 clamp(72px,9vw,110px);
}
.ac-hero-bg { position:absolute; inset:0; pointer-events:none; }
.ac-hero-bg::before {
  content:""; position:absolute; inset:0; opacity:.55;
  background-image:linear-gradient(rgba(255,255,255,.055) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(255,255,255,.055) 1px, transparent 1px);
  background-size:64px 64px;
  -webkit-mask-image:radial-gradient(115% 85% at 22% 4%, #000 32%, transparent 72%);
  mask-image:radial-gradient(115% 85% at 22% 4%, #000 32%, transparent 72%);
}
.ac-glow { position:absolute; border-radius:50%; filter:blur(96px); pointer-events:none; }
.ac-glow-1 { width:40rem; height:40rem; top:-16rem; right:-12rem; background:rgba(255,107,53,.30); animation:ac-drift 19s ease-in-out infinite; }
.ac-glow-2 { width:32rem; height:32rem; bottom:-16rem; left:-12rem; background:rgba(255,107,53,.14); animation:ac-drift 25s ease-in-out infinite reverse; }
@keyframes ac-drift { 0%,100%{transform:translate(0,0)} 50%{transform:translate(38px,-30px)} }
.ac-hero-grid {
  position:relative; z-index:1; display:grid; grid-template-columns:1.02fr .98fr;
  gap:clamp(36px,5vw,68px); align-items:center;
}
.ac-hero .ac-lead { color:rgba(255,255,255,.72); margin-top:24px; }
.ac-hero-cta { display:flex; flex-wrap:wrap; gap:14px; margin-top:38px; }
.ac-hero-meta {
  display:flex; flex-wrap:wrap; gap:10px 26px; margin-top:40px;
  padding-top:26px; border-top:1px solid var(--lineD);
  font-size:12.5px; color:rgba(255,255,255,.55);
}
.ac-hero-meta b { color:#fff; font-weight:600; }

/* Hero window cluster */
.ac-stack { position:relative; perspective:1600px; }
.ac-stack .ac-win { transition:transform .5s cubic-bezier(.2,.7,.2,1); }
.ac-stack-back {
  position:absolute; top:-4%; right:-4%; width:74%; z-index:1; opacity:.5;
  transform:rotate(2.6deg) scale(.94); filter:blur(.4px);
}
.ac-stack-front { position:relative; z-index:3; }
.ac-stack-mini {
  position:absolute; left:-9%; bottom:-13%; width:52%; z-index:4;
  box-shadow:0 34px 70px -24px rgba(0,0,0,.72);
}

${FRAME_CSS}

/* ---- Domain marquee ---- */
.ac-strip { background:var(--char2); padding:26px 0; border-top:1px solid var(--lineD); }
.ac-strip-row {
  display:flex; flex-wrap:wrap; align-items:center; justify-content:center;
  gap:14px 40px; font-size:12.5px; font-weight:600; letter-spacing:.13em;
  text-transform:uppercase; color:rgba(255,255,255,.42);
}
.ac-strip-row a { transition:color .25s ease; }
.ac-strip-row a:hover { color:var(--b); }

/* ---- Services ---- */
.ac-serv { display:grid; grid-template-columns:repeat(4,1fr); gap:clamp(16px,2vw,26px); margin-top:clamp(44px,5vw,64px); }
.ac-serv-card {
  background:var(--white); border:1px solid var(--line); border-radius:18px;
  padding:clamp(24px,2.4vw,32px); transition:transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s ease, border-color .35s ease;
}
.ac-serv-card:hover { transform:translateY(-6px); box-shadow:0 26px 54px -26px rgba(26,31,38,.28); border-color:rgba(255,107,53,.35); }
.ac-serv-ic {
  width:50px; height:50px; border-radius:14px; background:var(--bs); color:var(--b);
  display:grid; place-items:center; margin-bottom:22px;
}
.ac-serv-card h3 { font-size:18px; font-weight:600; }
.ac-serv-card p { margin-top:10px; font-size:14px; line-height:1.65; color:var(--muted); }

/* ---- Case studies ---- */
.ac-case { display:grid; grid-template-columns:1fr 1.08fr; gap:clamp(36px,5.5vw,76px); align-items:center; }
.ac-case + .ac-case { margin-top:clamp(72px,9vw,124px); }
.ac-case.rev .ac-case-media { order:-1; }
.ac-case-media { position:relative; }
.ac-case-media .ac-win { transition:transform .5s cubic-bezier(.2,.7,.2,1), box-shadow .5s ease; }
.ac-case-media:hover .ac-win { transform:translateY(-8px); box-shadow:0 46px 90px -34px rgba(10,8,6,.5); }
/* Sits before the frame in the DOM, so it paints behind it without a
   negative z-index (which would drop it behind the section background). */
.ac-case-glow { position:absolute; inset:12% 8%; border-radius:50%; filter:blur(70px); opacity:.24; }
.ac-case h3 { margin-top:16px; }
.ac-case-sum { margin-top:12px; font-size:16px; font-weight:600; color:var(--ink); }
.ac-dark .ac-case-sum { color:#fff; }
.ac-case p.ac-body { margin-top:16px; font-size:15px; line-height:1.75; color:var(--muted); max-width:34rem; }
.ac-dark .ac-case p.ac-body { color:rgba(255,255,255,.64); }
.ac-chips { display:flex; flex-wrap:wrap; gap:9px; margin-top:24px; }
.ac-chip {
  display:inline-flex; align-items:center; gap:7px; font-size:11.5px; font-weight:600;
  letter-spacing:.04em; padding:7px 13px; border-radius:999px;
  background:var(--bs); color:var(--bh); border:1px solid rgba(255,107,53,.18);
}
.ac-dark .ac-chip { background:rgba(255,255,255,.07); color:rgba(255,255,255,.78); border-color:var(--lineD); }
.ac-visit {
  display:inline-flex; align-items:center; gap:9px; margin-top:28px;
  font-size:12.5px; font-weight:700; letter-spacing:.13em; text-transform:uppercase;
  color:var(--b); border-bottom:1px solid rgba(255,107,53,.35); padding-bottom:5px;
  transition:gap .25s ease, border-color .25s ease;
}
.ac-visit:hover { gap:14px; border-color:var(--b); }
.ac-tag {
  display:inline-block; font-size:10.5px; font-weight:700; letter-spacing:.18em;
  text-transform:uppercase; padding:5px 11px; border-radius:999px; margin-left:10px;
  background:rgba(255,107,53,.12); color:var(--b); vertical-align:middle;
}

/* ---- Approach ---- */
.ac-steps { display:grid; grid-template-columns:repeat(4,1fr); gap:clamp(20px,2.5vw,34px); margin-top:clamp(44px,5vw,64px); }
.ac-step { position:relative; padding-top:30px; border-top:1px solid var(--lineD); }
.ac-step-n { font-family:Poppins,sans-serif; font-size:12px; font-weight:700; letter-spacing:.2em; color:var(--b); }
.ac-step h3 { font-size:19px; font-weight:600; margin-top:16px; display:flex; align-items:center; gap:10px; }
.ac-step p { margin-top:12px; font-size:14px; line-height:1.7; color:rgba(255,255,255,.6); }
.ac-step::after {
  content:""; position:absolute; top:-1px; left:0; width:0; height:1px; background:var(--b);
  transition:width .8s cubic-bezier(.2,.7,.2,1);
}
.ac-step.in::after { width:38%; }

/* ---- Stats ---- */
.ac-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:clamp(24px,3vw,44px); }
.ac-stat { text-align:center; }
.ac-stat-n { font-family:Poppins,sans-serif; font-size:clamp(44px,6vw,74px); font-weight:700; line-height:1; color:var(--b); letter-spacing:-.03em; }
.ac-stat-l { margin-top:14px; font-size:13.5px; line-height:1.5; color:var(--muted); }
.ac-dark .ac-stat-l { color:rgba(255,255,255,.6); }

/* ---- Why ---- */
.ac-why { display:grid; grid-template-columns:repeat(2,1fr); gap:clamp(28px,3.5vw,52px); margin-top:clamp(44px,5vw,60px); }
.ac-why-item { border-left:2px solid var(--b); padding-left:26px; }
.ac-why-item h3 { font-size:19px; font-weight:600; }
.ac-why-item p { margin-top:11px; font-size:14.5px; line-height:1.7; color:var(--muted); }

/* ---- Stack ---- */
.ac-tech { display:flex; flex-wrap:wrap; justify-content:center; gap:11px; margin-top:32px; }
.ac-tech span {
  font-size:12px; font-weight:600; letter-spacing:.1em; text-transform:uppercase;
  padding:9px 17px; border-radius:999px; border:1px solid var(--line); color:var(--muted); background:var(--white);
}

/* ---- CTA ---- */
.ac-cta-band { position:relative; overflow:hidden; background:var(--char); }
.ac-cta-band .ac-glow-1 { top:-22rem; right:-14rem; opacity:.85; }
.ac-cta-inner { position:relative; z-index:1; text-align:center; }
.ac-cta-row { display:flex; flex-wrap:wrap; justify-content:center; gap:14px; margin-top:38px; }
.ac-cta-alt {
  display:flex; flex-wrap:wrap; justify-content:center; gap:12px 34px; margin-top:34px;
  font-size:13.5px; color:rgba(255,255,255,.6);
}
.ac-cta-alt a { display:inline-flex; align-items:center; gap:9px; transition:color .2s ease; }
.ac-cta-alt a:hover { color:var(--b); }

/* ---- Footer ---- */
.ac-foot { background:var(--char2); color:#aeb4bd; padding:clamp(56px,7vw,84px) 0 0; }
.ac-foot-grid { display:grid; grid-template-columns:1.6fr 1fr 1fr 1fr; gap:clamp(28px,4vw,56px); }
.ac-foot-brand p { margin:22px 0 0; font-size:13.5px; line-height:1.85; color:#8f959f; max-width:23rem; }
.ac-foot h4 {
  font-size:11px; font-weight:700; letter-spacing:.2em; text-transform:uppercase;
  color:#fff; margin-bottom:22px;
}
.ac-foot-col a, .ac-foot-col span {
  display:flex; align-items:center; gap:10px; font-size:13.5px; line-height:1.5;
  margin-bottom:15px; color:#aeb4bd; transition:color .2s ease;
}
.ac-foot-col a:hover { color:var(--b); }
.ac-foot-bar {
  margin-top:clamp(44px,5vw,64px); border-top:1px solid rgba(255,255,255,.08);
  padding:26px 0; font-size:12.5px; color:#787f89;
}
.ac-foot-bar-row { display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; }
.ac-foot-legal { display:flex; flex-wrap:wrap; gap:18px; }
.ac-foot-legal a { color:#9aa1ab; text-decoration:none; }
.ac-foot-legal a:hover { color:var(--b); }

/* ---- Intro curtain ---- */
.ac-intro { position:fixed; inset:0; z-index:100; pointer-events:none; }
.ac-intro-panel { position:absolute; top:0; bottom:0; width:50.5%; background:var(--b); transition:transform 1s cubic-bezier(.76,0,.24,1); }
.ac-intro-panel.l { left:0; } .ac-intro-panel.r { right:0; }
.ac--in .ac-intro-panel.l { transform:translateX(-101%); }
.ac--in .ac-intro-panel.r { transform:translateX(101%); }
.ac-intro-mark { position:absolute; inset:0; display:grid; place-items:center; z-index:2; }
.ac--in .ac-intro-mark { opacity:0; transition:opacity .4s ease; }
.ac--in .ac-intro { visibility:hidden; transition:visibility 0s linear 1.1s; }
.ac-intro-inner { opacity:0; transform:translateY(14px); animation:ac-introIn .75s cubic-bezier(.2,.7,.2,1) forwards .15s; }
@keyframes ac-introIn { to { opacity:1; transform:none; } }
.ac-enter { opacity:0; transform:translateY(22px); }
.ac--in .ac-enter { animation:ac-up .85s cubic-bezier(.2,.7,.2,1) forwards; }
.ac--in .e1{animation-delay:.06s} .ac--in .e2{animation-delay:.16s}
.ac--in .e3{animation-delay:.26s} .ac--in .e4{animation-delay:.36s} .ac--in .e5{animation-delay:.48s}
@keyframes ac-up { to { opacity:1; transform:none; } }

/* ---- School Portal band ----
   The product's own palette, deliberately against the Acepsle orange used
   everywhere else on this page: a product has to look like itself, or it reads
   as one more section of the agency site. Buttons stay sharp — that is the
   Acepsle signature and it still applies inside a product band. */
.ac-sp { position:relative; overflow:hidden;
  background:linear-gradient(160deg,#002F90 0%,#001F66 100%); color:#fff; }
.ac-sp .ac-wrap { position:relative; z-index:2; }
.ac-sp-grid { display:grid; grid-template-columns:minmax(0,1.05fr) minmax(0,.95fr);
  gap:clamp(30px,5vw,60px); align-items:center; }
.ac-sp-logo { width:min(290px,74%); height:auto; display:block; margin-top:18px; }
.ac-sp-tag { display:block; margin-top:13px; font-size:.7rem; font-weight:600;
  letter-spacing:.2em; text-transform:uppercase; color:#7FD36B; }
.ac-sp h2 { margin-top:24px; font-size:clamp(1.8rem,3.3vw,2.8rem); line-height:1.05;
  letter-spacing:-.03em; font-weight:800; color:#fff; }
.ac-sp h2 em { font-style:normal; color:#9BE08E; }
.ac-sp-lede { margin-top:16px; color:#C9D8F5; font-size:1.01rem; line-height:1.7; max-width:56ch; }
.ac-sp-actions { display:flex; flex-wrap:wrap; gap:12px; margin-top:28px; }
/* The "a." prefix is load-bearing. ".ac a { color:inherit }" further up is a
   class AND an element, so it outranks a single-class rule and the label would
   inherit the band's white — invisible on the white button. The other buttons
   never showed this because their text is white anyway; this is the first one
   whose colour differs from the band it sits in. */
a.ac-btn-sp { background:#fff; color:#002F90; border-color:#fff; }
a.ac-btn-sp:hover { background:#D9ECFE; color:#001F66; border-color:#D9ECFE; }
a.ac-btn-sp-line { background:transparent; color:#fff; border-color:rgba(255,255,255,.5); }
a.ac-btn-sp-line:hover { background:rgba(255,255,255,.12); color:#fff; border-color:#fff; }
.ac-sp-points { display:grid; gap:13px; }
.ac-sp-point { padding:18px 20px; border-radius:14px;
  background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.15); }
.ac-sp-point b { display:block; font-size:.97rem; color:#fff; }
.ac-sp-point span { display:block; margin-top:5px; font-size:.87rem; color:#C9D8F5; line-height:1.55; }
.ac-sp-proof { margin-top:26px; padding-top:17px; border-top:1px solid rgba(255,255,255,.18);
  display:flex; flex-wrap:wrap; gap:8px 20px; align-items:center;
  font-size:.85rem; color:#A9C1EE; }
.ac-sp-proof strong { color:#fff; font-weight:600; letter-spacing:.04em; }
.ac-sp-proof a { color:#fff; font-weight:600; text-decoration:none;
  border-bottom:1px solid rgba(255,255,255,.4); }
.ac-sp-proof a:hover { border-bottom-color:#9BE08E; color:#9BE08E; }
.ac-sp-wave { position:absolute; left:0; right:0; bottom:-2px; z-index:1;
  width:100%; height:clamp(56px,8vw,110px); opacity:.4; pointer-events:none; }
@media (max-width:900px) {
  .ac-sp-grid { grid-template-columns:minmax(0,1fr); }
}

/* ---- Restructured page (/?next) ----
   Products lead and client work proves. The School Portal section sits on the
   page's OWN dark band here: its navy and green arrive in the screenshot and in
   small accents, which is what stops it reading as a foreign block. */
.acx-product .ac-sp-logo { width:min(260px,70%); }
.acx-product .ac-sp-tag { color:#7FD36B; }
/* The sizing rules live under ".ac-sp h2", and this band is not .ac-sp — so
   without these the headline renders at body size and its second half italic. */
.acx-product h2 { color:#fff; margin-top:24px; font-weight:800; line-height:1.05;
  letter-spacing:-.03em; font-size:clamp(1.8rem,3.3vw,2.8rem); }
.acx-product h2 em { color:#9BE08E; font-style:normal; }
.acx-product .ac-sp-lede { color:rgba(255,255,255,.78); }
.acx-product .ac-sp-proof { border-top-color:rgba(255,255,255,.16); color:rgba(255,255,255,.62); }
.acx-product .ac-sp-grid { align-items:center; }

/* the product's screen, framed the way every other project on the page is */
.acx-sp-media { position:relative; }

/* the four claims, as a row under the split rather than a stack beside it */
.acx-points { display:grid; gap:14px; margin-top:clamp(34px,4vw,52px);
  grid-template-columns:repeat(4,minmax(0,1fr)); }
@media (max-width:900px) { .acx-points { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media (max-width:520px) { .acx-points { grid-template-columns:minmax(0,1fr); } }
.acx-point { padding:18px 20px; border-radius:14px;
  background:rgba(255,255,255,.055); border:1px solid rgba(255,255,255,.12); }
.acx-point b { display:block; font-size:.95rem; color:#fff; }
.acx-point span { display:block; margin-top:5px; font-size:.86rem; line-height:1.55;
  color:rgba(255,255,255,.66); }

/* our other products, compact */
.acx-also { margin-top:clamp(48px,6vw,76px); padding-top:clamp(34px,4vw,48px);
  border-top:1px solid rgba(255,255,255,.14); }
.acx-also .ac-eyebrow { color:var(--b); }
.acx-also .ac-h3 { color:#fff; }
.acx-grid { margin-top:clamp(24px,3vw,34px); display:grid; gap:18px;
  grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr)); }
.acx-card { display:flex; flex-direction:column; border-radius:16px; overflow:hidden;
  background:rgba(255,255,255,.055); border:1px solid rgba(255,255,255,.12);
  transition:transform .2s ease, border-color .2s ease, background .2s ease; }
.acx-card:hover { transform:translateY(-3px); border-color:rgba(255,255,255,.26);
  background:rgba(255,255,255,.08); }
.acx-card-media { padding:14px 14px 0; }
.acx-card-body { padding:16px 18px 18px; }
.acx-dot { display:block; width:9px; height:9px; border-radius:50%; margin-bottom:10px; }
.acx-card-body h3 { font-family:Poppins,sans-serif; font-size:1.06rem; font-weight:600;
  color:#fff; letter-spacing:-.01em; }
.acx-card-body p { margin-top:5px; font-size:.87rem; line-height:1.55;
  color:rgba(255,255,255,.66); }
a.acx-link { display:inline-flex; align-items:center; gap:6px; margin-top:12px;
  font-size:.8rem; font-weight:600; color:var(--b); text-decoration:none; }
a.acx-link:hover { color:#fff; }

/* ---- Variant B: a band per product (/?v=b) ----
   The accent arrives as a top edge, a dot and the chips — not as a saturated
   background. School Portal keeps the only full-colour band above these, so the
   page reads as one flagship plus a family rather than seven competing brands. */
.acb-band { position:relative; background:#fff; padding-block:clamp(52px,6vw,86px); }
.acb-band:nth-of-type(even) { background:var(--paper); }
.acb-edge { position:absolute; inset:0 0 auto 0; height:4px; background:var(--acc); opacity:.9; }
.acb-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.05fr);
  gap:clamp(28px,5vw,64px); align-items:center; }
.acb-band.rev .acb-grid > *:first-child { order:2; }
@media (max-width:900px) {
  .acb-grid { grid-template-columns:minmax(0,1fr); }
  .acb-band.rev .acb-grid > *:first-child { order:0; }
}
.acb-sector { display:inline-flex; align-items:center; gap:9px; font-family:Inter,sans-serif;
  font-size:11px; font-weight:700; letter-spacing:.16em; text-transform:uppercase;
  color:var(--ink2); opacity:.72; }
.acb-dot { width:9px; height:9px; border-radius:50%; background:var(--acc); flex-shrink:0; }
.acb-name { margin-top:14px; font-family:Poppins,sans-serif; font-weight:600;
  font-size:clamp(1.7rem,3.2vw,2.5rem); letter-spacing:-.02em; color:var(--ink); line-height:1.05; }
.acb-sum { margin-top:8px; font-size:clamp(1rem,1.5vw,1.14rem); font-weight:600; color:var(--acc); }
.acb-body { margin-top:14px; max-width:52ch; }
.acb-band .ac-chip { border-color:color-mix(in srgb, var(--acc) 34%, transparent);
  color:var(--ink2); }
.acb-band .ac-visit { color:var(--acc); }
.acb-media { position:relative; }

/* ---- Responsive ---- */
@media (max-width:1000px) {
  .ac-serv { grid-template-columns:repeat(2,1fr); }
  .ac-steps { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:880px) {
  .ac-nav-links { display:none; }
  .ac-nav-cta .ac-btn-line { display:none; }
  .ac-burger { display:block; }
  /* Only an explicitly opened menu shows — never merely because the nav
     has picked up its scrolled background. */
  .ac-mobile.open {
    display:block;
    background:rgba(22,26,32,.97); -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px);
    border-top:1px solid var(--lineD); padding:12px 0 20px;
  }
  .ac-mobile a {
    display:block; padding:13px 0; font-size:13px; font-weight:600; letter-spacing:.14em;
    text-transform:uppercase; color:rgba(255,255,255,.78); border-bottom:1px solid var(--lineD);
  }
  .ac-hero-grid { grid-template-columns:1fr; }
  .ac-stack { margin-top:20px; }
  .ac-stack-mini { display:none; }
  .ac-stack-back { display:none; }
  .ac-case, .ac-case.rev { grid-template-columns:1fr; }
  .ac-case.rev .ac-case-media { order:0; }
  .ac-why { grid-template-columns:1fr; }
  .ac-foot-grid { grid-template-columns:1fr 1fr; }
  .ac-foot-brand { grid-column:1 / -1; }
}
@media (max-width:600px) {
  .ac-serv, .ac-steps, .ac-stats { grid-template-columns:1fr; }
  .ac-foot-grid { grid-template-columns:1fr; }
  .ac-hero-cta .ac-btn, .ac-cta-row .ac-btn { width:100%; }
  .ac-stat { text-align:left; }
}

@media (prefers-reduced-motion:reduce) {
  .ac-intro { display:none; }
  .ac-enter, .ac-reveal { opacity:1 !important; transform:none !important; animation:none !important; transition:none !important; }
  .ac-float, .ac-float2, .ac-glow, .ac-win, .ac-serv-card, .ac-case-media .ac-win { animation:none !important; }
  .ac-step::after { transition:none; }
}
`;
